import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Full-screen listing details page (route: /listings/:id).
 * Replaces the cramped CRM side panel — edit-then-close UX.
 */
export default function ListingDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [currencies, setCurrencies] = useState([]);
  const [units, setUnits] = useState("both");
  const [showFields, setShowFields] = useState([
    "sqft",
    "bedrooms",
    "bathrooms",
    "reception_rooms",
  ]);
  const [statsCardUrl, setStatsCardUrl] = useState(null);
  const [pbCardUrl, setPbCardUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [reels, setReels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [activePane, setActivePane] = useState("overview"); // overview | stats | photos | reels
  const [address, setAddress] = useState("");
  const [status, setStatus] = useState("active");
  const [listingUrl, setListingUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [openHouse, setOpenHouse] = useState({});
  const [aiFillOpen, setAiFillOpen] = useState(false);

  const fileRef = useRef();

  // ── Load listing + photos + reels ──
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/listings/${id}`);
      setListing(data);
      setStats(data?.stats || {});
      setAddress(data?.address || "");
      setStatus(data?.status || "active");
      setListingUrl(data?.listing_url || "");
      setNotes(data?.notes || "");
      setOpenHouse(data?.open_house || {});
      const cp = data?.card_prefs || {};
      if (cp.units) setUnits(cp.units);
      if (Array.isArray(cp.show_fields) && cp.show_fields.length > 0) {
        setShowFields(cp.show_fields);
      }
      // Photos for this listing
      try {
        const imgs = await api.get(`/images?listing_id=${id}`);
        setPhotos(imgs.data || []);
      } catch {}
      // Reels for this listing
      try {
        const reelRes = await api.get(`/reels?listing_id=${id}`);
        setReels(reelRes.data || []);
      } catch {}
    } catch (e) {
      toast.error("Listing not found");
      navigate("/app");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api
      .get("/listing-card/currencies")
      .then((r) => setCurrencies(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const [downloadingFlyer, setDownloadingFlyer] = useState(false);

  const downloadFlyer = async () => {
    setDownloadingFlyer(true);
    try {
      const res = await api.get(
        `/listings/${id}/flyer.pdf?share_base=${encodeURIComponent(window.location.origin)}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const slug = (listing?.address || "flyer").replace(/[^a-zA-Z0-9-_ ]/g, "").replace(/\s+/g, "_").slice(0, 60);
      a.download = `${slug}_flyer.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Flyer downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Flyer download failed");
    } finally {
      setDownloadingFlyer(false);
    }
  };

  const setStat = (k, v) => setStats((s) => ({ ...s, [k]: v }));

  const toggleField = (fid) =>
    setShowFields((f) =>
      f.includes(fid) ? f.filter((x) => x !== fid) : [...f, fid]
    );

  const fieldOpts = useMemo(
    () => [
      { id: "sqft", label: "Floor area", icon: "Ruler" },
      { id: "bedrooms", label: "Bedrooms", icon: "Bed" },
      { id: "bathrooms", label: "Bathrooms", icon: "Bath" },
      { id: "reception_rooms", label: "Reception rooms", icon: "Sofa" },
      { id: "garages", label: "Garages", icon: "Car" },
      { id: "year_built", label: "Year built", icon: "Calendar" },
      { id: "lot_sqft", label: "Lot size", icon: "Trees" },
    ],
    []
  );

  const previewStatsCard = async () => {
    setPreviewing(true);
    try {
      const res = await api.post(
        `/listings/${id}/card-preview`,
        { stats, town: notes, units, show_fields: showFields },
        { responseType: "blob" }
      );
      setStatsCardUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(res.data);
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const previewPresentedBy = async () => {
    setPreviewing(true);
    try {
      const res = await api.get(`/listings/${id}/presented-by-preview`, {
        responseType: "blob",
      });
      setPbCardUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(res.data);
      });
    } catch {
      toast.error("Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/listings/${id}`, {
        address: address.trim() || listing?.address,
        status,
        listing_url: listingUrl.trim() || null,
        notes: notes.trim() || null,
        stats,
        open_house: openHouse && (openHouse.date || openHouse.time || openHouse.address_override) ? openHouse : null,
        card_prefs: { units, show_fields: showFields },
      });
      toast.success("Listing saved");
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const tid = toast.loading(`Uploading 0/${files.length}…`);
    let done = 0;
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("listing_id", id);
        await api.post("/images", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        done += 1;
        toast.loading(`Uploading ${done}/${files.length}…`, { id: tid });
      } catch {}
    }
    toast.success(`${done}/${files.length} uploaded`, { id: tid });
    if (e.target) e.target.value = "";
    load();
  };

  const setCover = async (imageId) => {
    try {
      await api.patch(`/listings/${id}`, { cover_image_id: imageId });
      toast.success("Cover photo set");
      load();
    } catch {
      toast.error("Could not set cover");
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#070707] text-white flex items-center justify-center">
        <Icons.Loader2 className="animate-spin text-zinc-500" size={28} />
      </div>
    );
  }

  if (!listing) return null;

  const realtor = listing.realtor || {};
  const company = listing.company || {};
  const accent = company.color || "#A0A0A0";

  const tabs = [
    { id: "overview", label: "Overview", icon: "Home" },
    { id: "stats", label: "Stats card", icon: "Tag" },
    { id: "photos", label: `Photos (${photos.length})`, icon: "ImageIcon" },
    { id: "reels", label: `Reels (${reels.length})`, icon: "Film" },
    { id: "documents", label: "Documents", icon: "FolderOpen" },
  ];

  return (
    <div className="fixed inset-0 bg-[#070707] text-white overflow-y-auto" data-testid="listing-details-page">
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-[#070707]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate("/app")}
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white"
            data-testid="listing-details-back"
          >
            <Icons.ArrowLeft size={16} />
            Back to workspace
          </button>
          <div className="flex-1" />
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
            Listing details
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-10 pb-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 mb-3">
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ background: accent }}
              />
              <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                {company.name || "—"} · {realtor.name || "—"}
              </span>
            </div>
            <h1 className="font-display text-3xl md:text-5xl text-white leading-tight break-words">
              {address || listing.address}
            </h1>
            <div className="mt-3 text-sm text-zinc-500">
              {stats.bedrooms ? `${stats.bedrooms} bed · ` : ""}
              {stats.bathrooms ? `${stats.bathrooms} bath · ` : ""}
              {stats.sqft ? `${Number(stats.sqft).toLocaleString()} sqft · ` : ""}
              {stats.price
                ? `${stats.currency || "GBP"} ${Number(stats.price).toLocaleString()}`
                : "Price not set"}
            </div>
            {/* Quick links to Google Earth / Maps / Street View — useful for
                grabbing the aerial screenshot the AI Drone feature needs,
                and for double-checking realtor-supplied address spelling. */}
            {(address || listing.address) && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 mr-1">
                  Open in
                </span>
                <a
                  href={`https://earth.google.com/web/search/${encodeURIComponent(address || listing.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md border border-white/10 bg-black/40 text-zinc-300 hover:border-amber-400/40 hover:text-amber-200 transition-colors"
                  title="Google Earth — useful for capturing the top-down aerial screenshot the AI Drone feature needs."
                  data-testid="listing-details-open-earth"
                >
                  <Icons.Globe2 size={12} />
                  Google Earth
                </a>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address || listing.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md border border-white/10 bg-black/40 text-zinc-300 hover:border-amber-400/40 hover:text-amber-200 transition-colors"
                  title="Google Maps satellite / map view."
                  data-testid="listing-details-open-maps"
                >
                  <Icons.MapPin size={12} />
                  Google Maps
                </a>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address || listing.address)}&travelmode=driving&layer=c`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md border border-white/10 bg-black/40 text-zinc-300 hover:border-amber-400/40 hover:text-amber-200 transition-colors"
                  title="Google Maps Street View — useful for capturing a clean ground-level reference photo of the property."
                  data-testid="listing-details-open-streetview"
                >
                  <Icons.Footprints size={12} />
                  Street View
                </a>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard
                      .writeText(address || listing.address || "")
                      .then(() => toast.success("Address copied to clipboard"))
                      .catch(() => toast.error("Copy failed"));
                  }}
                  className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md border border-white/10 bg-black/40 text-zinc-400 hover:border-white/30 hover:text-white transition-colors"
                  title="Copy address to clipboard."
                  data-testid="listing-details-copy-address"
                >
                  <Icons.Copy size={12} />
                  Copy
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <FloorPlanQuickUpload
              listingId={id}
              accent={accent}
              onJump={() => setActivePane("documents")}
            />
            <button
              onClick={downloadFlyer}
              disabled={downloadingFlyer}
              className="border border-white/15 hover:border-white/30 text-white rounded-lg px-4 py-2.5 text-sm flex items-center gap-2 transition-all disabled:opacity-40"
              title="Print-ready A4 PDF flyer with cover photo, stats, headshot, QR code linking to the share page, and open-house banner."
              data-testid="listing-details-flyer"
            >
              {downloadingFlyer ? (
                <Icons.Loader2 className="animate-spin" size={14} />
              ) : (
                <Icons.Printer size={14} />
              )}
              Download flyer (PDF)
            </button>
            <button
              onClick={() => setAiFillOpen(true)}
              className="border border-amber-300/40 hover:border-amber-300 bg-amber-300/5 hover:bg-amber-300/10 text-amber-200 rounded-lg px-4 py-2.5 text-sm flex items-center gap-2 transition-all"
              title="AI Fill — paste a listing URL or text and gpt-5.2 fills in price, beds, baths, sqft, agent details, etc."
              data-testid="listing-details-ai-fill"
            >
              <Icons.Sparkles size={14} />
              AI Fill from URL
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="bg-white text-black font-medium rounded-lg px-5 py-2.5 hover:bg-zinc-200 active:scale-[0.98] disabled:opacity-30 transition-all flex items-center gap-2 text-sm"
              data-testid="listing-details-save"
            >
              {saving ? (
                <Icons.Loader2 className="animate-spin" size={14} />
              ) : (
                <Icons.Check size={14} />
              )}
              Save changes
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-10 border-b border-white/10 flex gap-1 overflow-x-auto scrollbar-hide">
          {tabs.map((t) => {
            const I = Icons[t.icon] || Icons.Circle;
            const on = activePane === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActivePane(t.id)}
                className={`relative flex items-center gap-2 px-5 py-3 text-sm transition-colors ${
                  on ? "text-white" : "text-zinc-500 hover:text-white"
                }`}
                data-testid={`listing-details-tab-${t.id}`}
              >
                <I size={14} />
                {t.label}
                {on && (
                  <span
                    className="absolute bottom-[-1px] left-0 right-0 h-[2px]"
                    style={{ background: accent }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Pane content */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24 space-y-12">
        {activePane === "overview" && (
          <div className="grid md:grid-cols-2 gap-8">
            {/* Property fields */}
            <div className="space-y-6">
              <Section title="Property">
                <Field label="Address">
                  <input
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className={inputCls}
                    data-testid="ld-address"
                  />
                </Field>
                <Field label="Status">
                  <div className="flex gap-1.5">
                    {["active", "pending", "sold", "draft"].map((s) => (
                      <button
                        key={s}
                        onClick={() => setStatus(s)}
                        className={`text-xs uppercase tracking-wider rounded-md px-3 py-1.5 border transition-all ${
                          status === s
                            ? "bg-white text-black border-white"
                            : "bg-white/5 border-white/10 hover:border-white/30 text-zinc-300"
                        }`}
                        data-testid={`ld-status-${s}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Public listing URL (realtor.ca, Rightmove, etc.)">
                  <input
                    value={listingUrl}
                    onChange={(e) => setListingUrl(e.target.value)}
                    placeholder="https://…"
                    className={inputCls}
                    data-testid="ld-listing-url"
                  />
                </Field>
                <Field label="Town / Notes">
                  <input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. Mayfair, London"
                    className={inputCls}
                    data-testid="ld-notes"
                  />
                </Field>
              </Section>

              <Section title="Open house">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Date">
                    <input
                      value={openHouse?.date || ""}
                      onChange={(e) => setOpenHouse((s) => ({ ...s, date: e.target.value }))}
                      placeholder="Sat 8 Oct 2025"
                      className={inputCls}
                      data-testid="ld-oh-date"
                    />
                  </Field>
                  <Field label="Time">
                    <input
                      value={openHouse?.time || ""}
                      onChange={(e) => setOpenHouse((s) => ({ ...s, time: e.target.value }))}
                      placeholder="2 – 4 PM"
                      className={inputCls}
                      data-testid="ld-oh-time"
                    />
                  </Field>
                </div>
                <Field label="Address override (blank = listing address)">
                  <input
                    value={openHouse?.address_override || ""}
                    onChange={(e) => setOpenHouse((s) => ({ ...s, address_override: e.target.value }))}
                    placeholder="Same as listing"
                    className={inputCls}
                    data-testid="ld-oh-address"
                  />
                </Field>
                <p className="text-[11px] text-zinc-500 leading-relaxed">
                  Saved on the listing — every reel can pull this in via the
                  Reel Overlays panel and show it as a bottom banner or pill.
                </p>
              </Section>

              <Section title="Price">
                <div className="grid grid-cols-[1fr_220px] gap-3">
                  <Field label="Amount">
                    <input
                      type="number"
                      value={stats.price ?? ""}
                      onChange={(e) =>
                        setStat("price", e.target.value === "" ? null : Number(e.target.value))
                      }
                      placeholder="1150000"
                      className={`${inputCls} font-mono`}
                      data-testid="ld-price"
                    />
                  </Field>
                  <Field label="Currency">
                    <select
                      value={stats.currency || "GBP"}
                      onChange={(e) => setStat("currency", e.target.value)}
                      className={inputCls}
                      data-testid="ld-currency"
                    >
                      {currencies.map((c) => (
                        <option key={c.code} value={c.code} className="bg-black">
                          {c.symbol} · {c.code}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <Field label="Label (optional, e.g. 'Guide price')">
                  <input
                    value={stats.price_label || ""}
                    onChange={(e) => setStat("price_label", e.target.value)}
                    placeholder="Guide price"
                    className={inputCls}
                    data-testid="ld-price-label"
                  />
                </Field>
              </Section>

              <Section title="Bedrooms / Bathrooms / Sizes">
                <div className="grid grid-cols-2 gap-3">
                  <NumField label="Bedrooms" value={stats.bedrooms} onChange={(v) => setStat("bedrooms", v)} testid="ld-bedrooms" />
                  <NumField label="Bathrooms" value={stats.bathrooms} onChange={(v) => setStat("bathrooms", v)} testid="ld-bathrooms" />
                  <NumField label="Reception rooms" value={stats.reception_rooms} onChange={(v) => setStat("reception_rooms", v)} testid="ld-reception" />
                  <NumField label="Garages" value={stats.garages} onChange={(v) => setStat("garages", v)} testid="ld-garages" />
                  <NumField label="Floor area (sq ft)" value={stats.sqft} onChange={(v) => setStat("sqft", v)} testid="ld-sqft" />
                  <NumField label="Floor area (m²)" value={stats.sqm} onChange={(v) => setStat("sqm", v)} testid="ld-sqm" />
                  <NumField label="Year built" value={stats.year_built} onChange={(v) => setStat("year_built", v)} testid="ld-year" />
                  <NumField label="Lot size (sq ft)" value={stats.lot_sqft} onChange={(v) => setStat("lot_sqft", v)} testid="ld-lot" />
                </div>
                <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
                  Leave any field blank to hide it from cards. Floor area auto-converts when only one is set.
                </p>
              </Section>
            </div>

            {/* Realtor card preview & quick info */}
            <div className="space-y-6">
              <Section title="Presented by">
                <div className="bg-white/5 border border-white/10 rounded-xl p-5 flex items-center gap-4">
                  {realtor.headshot_image_id ? (
                    <img
                      src={mediaUrl(`/api/public/images/${realtor.headshot_image_id}`)}
                      alt=""
                      className="w-16 h-16 rounded-full object-cover shrink-0"
                      style={{ boxShadow: `0 0 0 2px ${accent}` }}
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                      <Icons.User size={20} className="text-zinc-600" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-base font-medium text-white truncate">
                      {realtor.name || "—"}
                    </div>
                    <div className="text-xs text-zinc-500 truncate">
                      {realtor.brokerage_name || company.name || ""}
                    </div>
                    <div className="text-[11px] text-zinc-600 mt-1 space-x-2">
                      {realtor.phone && <span>{realtor.phone}</span>}
                      {realtor.email && (
                        <>
                          <span>·</span>
                          <span className="truncate">{realtor.email}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={previewPresentedBy}
                  disabled={previewing}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs text-white border border-white/15 rounded-lg px-3 py-2 hover:bg-white/5 disabled:opacity-40"
                  data-testid="ld-pby-preview"
                >
                  {previewing ? <Icons.Loader2 size={12} className="animate-spin" /> : <Icons.Eye size={12} />}
                  Preview "Presented by" intro card
                </button>
                {pbCardUrl && (
                  <div className="mt-3 aspect-video bg-black border border-white/10 rounded-xl overflow-hidden">
                    <img src={pbCardUrl} alt="Presented by preview" className="w-full h-full object-contain" data-testid="ld-pby-preview-img" />
                  </div>
                )}
              </Section>

              <Section title="Cover photo">
                {listing.cover_image_id ? (
                  <img
                    src={mediaUrl(`/api/public/images/${listing.cover_image_id}`)}
                    alt=""
                    className="w-full aspect-video object-cover rounded-xl border border-white/10"
                  />
                ) : (
                  <div className="w-full aspect-video flex items-center justify-center bg-white/5 border border-dashed border-white/15 rounded-xl text-zinc-600 text-sm">
                    No cover photo set — pick one from the Photos tab
                  </div>
                )}
              </Section>
            </div>
          </div>
        )}

        {activePane === "stats" && (
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <Section title="Display">
                <Field label="Area units">
                  <div className="flex gap-1.5">
                    {[
                      { id: "sqft", label: "sq ft" },
                      { id: "sqm", label: "m²" },
                      { id: "both", label: "Both" },
                    ].map((u) => (
                      <button
                        key={u.id}
                        onClick={() => setUnits(u.id)}
                        className={`text-xs rounded-lg px-4 py-2 border transition-all ${
                          units === u.id
                            ? "bg-white text-black border-white"
                            : "bg-black/40 border-white/10 hover:border-white/30 text-white"
                        }`}
                        data-testid={`ld-units-${u.id}`}
                      >
                        {u.label}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Icons to show on the card">
                  <div className="flex flex-wrap gap-1.5">
                    {fieldOpts.map((f) => {
                      const I = Icons[f.icon] || Icons.Circle;
                      const on = showFields.includes(f.id);
                      return (
                        <button
                          key={f.id}
                          onClick={() => toggleField(f.id)}
                          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 border text-[12px] transition-all ${
                            on
                              ? "bg-white text-black border-white"
                              : "bg-black/40 border-white/10 hover:border-white/30 text-white"
                          }`}
                          data-testid={`ld-field-${f.id}`}
                        >
                          <I size={12} />
                          {f.label}
                        </button>
                      );
                    })}
                  </div>
                </Field>
                <button
                  onClick={previewStatsCard}
                  disabled={previewing}
                  className="mt-2 inline-flex items-center gap-1.5 text-sm text-white border border-white/15 rounded-lg px-4 py-2.5 hover:bg-white/5 disabled:opacity-40"
                  data-testid="ld-stats-preview"
                >
                  {previewing ? (
                    <Icons.Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Icons.Eye size={14} />
                  )}
                  Refresh preview
                </button>
              </Section>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 mb-3">
                Stats card · 1920×1080
              </div>
              <div className="aspect-video bg-[#141B2D] border border-white/10 rounded-xl overflow-hidden flex items-center justify-center">
                {previewing && !statsCardUrl ? (
                  <Icons.Loader2 size={28} className="animate-spin text-zinc-600" />
                ) : statsCardUrl ? (
                  <img
                    src={statsCardUrl}
                    alt="Stats card preview"
                    className="w-full h-full object-contain"
                    data-testid="ld-stats-card-img"
                  />
                ) : (
                  <button
                    onClick={previewStatsCard}
                    className="text-zinc-400 text-sm hover:text-white"
                  >
                    Click to render preview
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {activePane === "photos" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400">
                {photos.length} photo{photos.length === 1 ? "" : "s"}
              </div>
              <button
                onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-1.5 text-sm border border-white/15 rounded-lg px-4 py-2 hover:bg-white/5"
                data-testid="ld-photos-add"
              >
                <Icons.Plus size={14} /> Add photos
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={uploadPhotos}
              />
            </div>
            {photos.length === 0 ? (
              <div className="border border-dashed border-white/10 rounded-xl p-16 text-center">
                <Icons.Image className="mx-auto text-zinc-700" size={32} />
                <div className="text-sm text-zinc-500 mt-3">
                  No photos yet. Drop in JPGs/PNGs to start a reel.
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {photos.map((p) => (
                  <div
                    key={p.id}
                    className={`group relative aspect-video bg-black rounded-lg overflow-hidden border ${
                      listing.cover_image_id === p.id
                        ? "border-amber-400"
                        : "border-white/10"
                    }`}
                    data-testid={`ld-photo-${p.id}`}
                  >
                    <img
                      src={mediaUrl(`/api/public/images/${p.id}`)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => setCover(p.id)}
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-black/80 text-[10px] uppercase tracking-wider rounded px-2 py-1 hover:bg-amber-400 hover:text-black transition-all"
                    >
                      {listing.cover_image_id === p.id ? "Cover" : "Set cover"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activePane === "reels" && (
          <div className="space-y-3">
            {reels.length === 0 ? (
              <div className="border border-dashed border-white/10 rounded-xl p-16 text-center">
                <Icons.Film className="mx-auto text-zinc-700" size={32} />
                <div className="text-sm text-zinc-500 mt-3">
                  No reels yet for this listing.
                </div>
                <button
                  onClick={() => navigate("/app")}
                  className="mt-4 inline-flex items-center gap-1.5 text-sm text-white border border-white/15 rounded-lg px-4 py-2 hover:bg-white/5"
                >
                  <Icons.ArrowLeft size={12} /> Go to workspace to compose one
                </button>
              </div>
            ) : (
              reels.map((r) => (
                <div
                  key={r.id}
                  className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 flex items-center gap-3"
                  data-testid={`ld-reel-${r.id}`}
                >
                  <Icons.Film size={14} className="text-zinc-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{r.title}</div>
                    <div className="text-[11px] text-zinc-500">
                      {r.status} · {r.shot_job_ids?.length || 0} shots
                    </div>
                  </div>
                  <span
                    className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded ${
                      r.status === "done"
                        ? "bg-emerald-500/10 text-emerald-300"
                        : r.status === "failed"
                        ? "bg-red-500/10 text-red-300"
                        : "bg-amber-500/10 text-amber-300"
                    }`}
                  >
                    {r.status}
                  </span>
                </div>
              ))
            )}
          </div>
        )}

        {activePane === "documents" && (
          <DocumentsPane listingId={id} accent={accent} />
        )}
      </div>
      {aiFillOpen && (
        <AiFillModal
          listingId={id}
          currentRealtorId={listing?.realtor_id}
          onClose={() => setAiFillOpen(false)}
          onApplied={() => {
            setAiFillOpen(false);
            load();
          }}
        />
      )}
    </div>
  );
}

const inputCls =
  "w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30";

function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 pb-2 border-b border-white/5">
        {title}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] text-zinc-400 mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="text-[11px] text-zinc-400 mb-1.5">{label}</div>
      <input
        type="number"
        value={value ?? ""}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "") return onChange(null);
          const n = Number(v);
          onChange(Number.isFinite(n) ? n : null);
        }}
        placeholder="—"
        className={`${inputCls} font-mono`}
        data-testid={testid}
      />
    </label>
  );
}

function AiFillModal({ listingId, currentRealtorId, onClose, onApplied }) {
  const [tab, setTab] = useState("url"); // url | text
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [applying, setApplying] = useState(false);
  // Per-group apply toggles — let users cherry-pick what to write back
  const [opts, setOpts] = useState({
    apply_address: true,
    apply_stats: true,
    apply_open_house: true,
    apply_listing_url: true,
    apply_notes: true,
    apply_realtor: false,
  });

  const fetchPreview = async () => {
    setLoading(true);
    setData(null);
    try {
      const payload = tab === "url" ? { url: url.trim() } : { text: text.trim() };
      const { data: res } = await api.post(`/listings/ai-fill`, payload);
      setData(res?.data || null);
      if (!res?.data) toast.warning("No data extracted — try the other tab");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI Fill failed");
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!data) return;
    setApplying(true);
    try {
      const { data: res } = await api.post(`/listings/${listingId}/ai-fill/apply`, {
        data,
        ...opts,
      });
      const n = (res?.fields_applied || []).length;
      toast.success(`Applied ${n} field${n === 1 ? "" : "s"} ${res?.realtor_updated ? "+ realtor" : ""}`);
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="ai-fill-overlay"
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0A0A0A] border border-white/10 rounded-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icons.Sparkles size={16} className="text-amber-300" />
            <div className="font-display text-xl text-white">AI Fill</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 ml-2">
              gpt-5.2
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-white"
            data-testid="ai-fill-close"
          >
            <Icons.X size={18} />
          </button>
        </div>

        <div className="text-[12px] text-zinc-400 mb-4 leading-relaxed">
          Paste a public listing URL (realtor.ca, Rightmove, Zoopla, MLS, etc.)
          and gpt-5.2 will extract the address, price, beds/baths/sqft, agent
          contact, open-house, and notes. Some sites block bots — if the URL
          fails, switch to the "Paste text" tab and paste the listing description.
        </div>

        {/* Source picker */}
        <div className="flex gap-1 mb-4 bg-white/5 border border-white/10 rounded-lg p-1 w-fit">
          <button
            onClick={() => setTab("url")}
            className={`px-4 py-1.5 text-xs rounded-md transition-all ${
              tab === "url" ? "bg-white text-black" : "text-zinc-400 hover:text-white"
            }`}
            data-testid="ai-fill-tab-url"
          >
            From URL
          </button>
          <button
            onClick={() => setTab("text")}
            className={`px-4 py-1.5 text-xs rounded-md transition-all ${
              tab === "text" ? "bg-white text-black" : "text-zinc-400 hover:text-white"
            }`}
            data-testid="ai-fill-tab-text"
          >
            Paste text
          </button>
        </div>

        {tab === "url" ? (
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.realtor.ca/listing/..."
            className={inputCls}
            data-testid="ai-fill-url"
          />
        ) : (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste the full listing description here…"
            rows={6}
            className={`${inputCls} font-mono text-xs leading-relaxed`}
            data-testid="ai-fill-text"
          />
        )}

        <button
          onClick={fetchPreview}
          disabled={loading || (tab === "url" ? !url.trim() : text.trim().length < 30)}
          className="mt-3 w-full bg-amber-300 text-black font-medium rounded-lg py-2.5 hover:bg-amber-200 disabled:opacity-30 flex items-center justify-center gap-2"
          data-testid="ai-fill-extract"
        >
          {loading ? <Icons.Loader2 size={14} className="animate-spin" /> : <Icons.Wand2 size={14} />}
          {loading ? "gpt-5.2 reading…" : "Extract details"}
        </button>

        {data && (
          <div className="mt-5 border-t border-white/10 pt-5 space-y-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
              Preview — pick what to apply
            </div>

            {/* Per-group preview rows */}
            <PreviewRow
              label="Address"
              checked={opts.apply_address}
              onToggle={() => setOpts((o) => ({ ...o, apply_address: !o.apply_address }))}
              value={data.address}
              testid="ai-fill-row-address"
            />
            <PreviewRow
              label="Listing URL"
              checked={opts.apply_listing_url}
              onToggle={() => setOpts((o) => ({ ...o, apply_listing_url: !o.apply_listing_url }))}
              value={data.listing_url}
              testid="ai-fill-row-url"
            />
            <PreviewRow
              label="Town / Notes"
              checked={opts.apply_notes}
              onToggle={() => setOpts((o) => ({ ...o, apply_notes: !o.apply_notes }))}
              value={data.notes}
              testid="ai-fill-row-notes"
            />

            <PreviewGroup
              label="Stats"
              checked={opts.apply_stats}
              onToggle={() => setOpts((o) => ({ ...o, apply_stats: !o.apply_stats }))}
              testid="ai-fill-row-stats"
            >
              {data.stats && Object.entries(data.stats).filter(([, v]) => v !== null && v !== "").length > 0 ? (
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] text-zinc-300 font-mono">
                  {Object.entries(data.stats || {})
                    .filter(([, v]) => v !== null && v !== "")
                    .map(([k, v]) => (
                      <div key={k} className="flex justify-between">
                        <span className="text-zinc-500">{k}</span>
                        <span>{String(v)}</span>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-[12px] text-zinc-600 italic">— not detected —</div>
              )}
            </PreviewGroup>

            <PreviewGroup
              label="Open house"
              checked={opts.apply_open_house}
              onToggle={() => setOpts((o) => ({ ...o, apply_open_house: !o.apply_open_house }))}
              testid="ai-fill-row-oh"
            >
              {data.open_house && (data.open_house.date || data.open_house.time) ? (
                <div className="text-[12px] text-zinc-300">
                  {[data.open_house.date, data.open_house.time].filter(Boolean).join(" · ")}
                </div>
              ) : (
                <div className="text-[12px] text-zinc-600 italic">— not detected —</div>
              )}
            </PreviewGroup>

            <PreviewGroup
              label="Realtor"
              checked={opts.apply_realtor}
              onToggle={() => setOpts((o) => ({ ...o, apply_realtor: !o.apply_realtor }))}
              testid="ai-fill-row-realtor"
              warning={
                currentRealtorId
                  ? "This will overwrite the existing realtor's name/contact details."
                  : "No realtor attached to this listing — toggle ignored."
              }
            >
              {data.realtor && Object.values(data.realtor).some(Boolean) ? (
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] text-zinc-300 font-mono">
                  {Object.entries(data.realtor || {})
                    .filter(([, v]) => v)
                    .map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-3 truncate">
                        <span className="text-zinc-500 shrink-0">{k}</span>
                        <span className="truncate">{String(v)}</span>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-[12px] text-zinc-600 italic">— not detected —</div>
              )}
            </PreviewGroup>

            <button
              onClick={apply}
              disabled={applying}
              className="w-full bg-emerald-500 text-black font-medium rounded-lg py-2.5 hover:bg-emerald-400 disabled:opacity-30 flex items-center justify-center gap-2"
              data-testid="ai-fill-apply"
            >
              {applying ? <Icons.Loader2 size={14} className="animate-spin" /> : <Icons.Check size={14} />}
              {applying ? "Applying…" : "Apply selected fields"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PreviewRow({ label, checked, onToggle, value, testid }) {
  return (
    <label
      className="flex items-start gap-3 cursor-pointer group"
      data-testid={testid}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="mt-1 w-4 h-4 accent-amber-300"
      />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-0.5">
          {label}
        </div>
        <div className="text-[13px] text-white truncate">
          {value || <span className="text-zinc-600 italic">— not detected —</span>}
        </div>
      </div>
    </label>
  );
}

function PreviewGroup({ label, checked, onToggle, children, warning, testid }) {
  return (
    <div data-testid={testid}>
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="mt-1 w-4 h-4 accent-amber-300"
        />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1.5">
            {label}
          </div>
          {children}
          {checked && warning && (
            <div className="mt-1.5 text-[10px] text-amber-300/70">{warning}</div>
          )}
        </div>
      </label>
    </div>
  );
}


const DOC_KIND_OPTIONS = [
  { id: "floorplan", label: "Floor plan" },
  { id: "brochure", label: "Brochure" },
  { id: "epc", label: "Energy (EPC)" },
  { id: "deed", label: "Deed / Title" },
  { id: "survey", label: "Survey" },
  { id: "other", label: "Other" },
];

function DocumentsPane({ listingId, accent }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [pickKind, setPickKind] = useState("floorplan");
  const fileInput = useRef();

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/listings/${listingId}/documents`);
      setDocs(data || []);
    } catch {
      // silent
    }
  }, [listingId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", pickKind);
      fd.append("label", f.name);
      await api.post(`/listings/${listingId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      e.target.value = "";
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (docId) => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await api.delete(`/listings/${listingId}/documents/${docId}`);
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="documents-pane">
      <div className="bg-white/[0.02] border border-white/10 rounded-xl p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
              Document type
            </div>
            <select
              value={pickKind}
              onChange={(e) => setPickKind(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
              data-testid="documents-kind-select"
            >
              {DOC_KIND_OPTIONS.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.label}
                </option>
              ))}
            </select>
          </div>
          <input
            ref={fileInput}
            type="file"
            accept="application/pdf,image/*"
            onChange={onPick}
            className="hidden"
            data-testid="documents-file-input"
          />
          <button
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            className="font-medium rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-30"
            style={{ background: accent, color: "#000" }}
            data-testid="documents-upload-button"
          >
            {uploading ? (
              <Icons.Loader2 size={13} className="animate-spin" />
            ) : (
              <Icons.Upload size={13} />
            )}
            Upload
          </button>
        </div>
        <div className="text-[11px] text-zinc-600 mt-2">
          PDFs, images. Max 25 MB. These appear under each shared reel for
          viewers to download.
        </div>
      </div>

      {docs.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-xl p-12 text-center">
          <Icons.FolderOpen className="mx-auto text-zinc-700" size={28} />
          <div className="text-sm text-zinc-500 mt-2">
            No documents yet — upload the floor plan to give buyers what they
            actually want before viewing.
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map((d) => (
            <div
              key={d.id}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-white/10 bg-white/[0.02]"
              data-testid={`documents-row-${d.id}`}
            >
              <Icons.FileText size={14} className="text-zinc-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{d.label || d.filename}</div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
                  {d.kind}
                  {d.size > 0 && (
                    <span className="text-zinc-600 normal-case tracking-normal ml-2">
                      · {(d.size / 1024).toFixed(0)} KB
                    </span>
                  )}
                </div>
              </div>
              <a
                href={`${BACKEND_URL}/api/public/documents/${d.id}`}
                target="_blank"
                rel="noreferrer"
                className="text-zinc-400 hover:text-white p-1.5"
                title="Preview"
              >
                <Icons.ExternalLink size={13} />
              </a>
              <button
                onClick={() => remove(d.id)}
                className="text-zinc-500 hover:text-red-400 p-1.5"
                data-testid={`documents-row-${d.id}-delete`}
              >
                <Icons.Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FloorPlanQuickUpload({ listingId, accent, onJump }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/listings/${listingId}/documents`);
      setDoc((data || []).find((d) => d.kind === "floorplan") || null);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [listingId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", "floorplan");
      fd.append("label", "Floor plan");
      await api.post(`/listings/${listingId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Floor plan uploaded — viewers can open it instantly.");
      e.target.value = "";
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (loading) return null;

  // If a floor plan exists already, show a confirmation chip with "Replace"
  // and "Open Documents tab" affordances.
  if (doc) {
    return (
      <button
        onClick={onJump}
        className="border rounded-lg px-3 py-2.5 text-sm flex items-center gap-2 transition-all hover:bg-white/5"
        style={{ borderColor: `${accent}60`, color: accent }}
        data-testid="floor-plan-quick-status"
        title="Floor plan uploaded — open the Documents tab to manage it."
      >
        <Icons.CheckCircle2 size={14} />
        Floor plan ready
      </button>
    );
  }

  // Otherwise, single-click upload.
  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="application/pdf,image/*"
        onChange={onPick}
        className="hidden"
        data-testid="floor-plan-quick-file"
      />
      <button
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="border border-white/15 hover:border-white/30 text-white rounded-lg px-3 py-2.5 text-sm flex items-center gap-2 transition-all disabled:opacity-40"
        title="Upload floor plan — viewers will see it inline on the share page (no email-capture friction)."
        data-testid="floor-plan-quick-upload"
      >
        {uploading ? (
          <Icons.Loader2 className="animate-spin" size={14} />
        ) : (
          <Icons.Ruler size={14} />
        )}
        Upload floor plan
      </button>
    </>
  );
}

